import {
  Collections,
  getDirectusClient,
  imageFields,
  readItems
} from 'app/utility/directus';
import { mapSpecialistPreviewFields } from 'app/adapters/specialists';
import { normalizeFilterParam } from 'app/utility/helpers';
import { getPlaceholderDataUrl, isGIF } from 'app/utility/image';

export interface IGetSpecialistsParams {
  id?: string | string[];
  slug?: string | string[];
  branchSlug?: string | string[];
  serviceSlug?: string | string[];
  categorySlug?: string | string[];
  status?: string;
  page?: number;
  limit?: number;
  fetchPreviousPages?: boolean;
  skipPlaceholders?: boolean;
}

export default async function getSpecialists({
  id,
  slug,
  branchSlug,
  serviceSlug,
  categorySlug,
  status,
  page = 1,
  limit = 100,
  fetchPreviousPages = false,
  skipPlaceholders = false
}: IGetSpecialistsParams = {}) {
  try {
    const directusClient = getDirectusClient();
    const collectionName = Collections.Specialists;
    const filter = {
      id: id?.length ? { _in: normalizeFilterParam(id) } : undefined,
      slug: slug?.length ? { _in: normalizeFilterParam(slug) } : undefined,
      branches: branchSlug?.length
        ? { branch: { slug: { _in: normalizeFilterParam(branchSlug) } } }
        : undefined,
      services: serviceSlug?.length
        ? { service: { slug: { _in: normalizeFilterParam(serviceSlug) } } }
        : undefined,
      category: categorySlug?.length
        ? { slug: { _in: normalizeFilterParam(categorySlug) } }
        : undefined,
      status: typeof status === 'string' && status ? status : undefined
    };

    const items = await directusClient.request<Directus.SpecialistPreview[]>(
      readItems(collectionName, {
        filter,
        page: fetchPreviousPages ? 1 : page,
        limit: fetchPreviousPages ? page * limit : limit,
        sort: ['sort', 'name'],
        fields: [
          'id',
          'status',
          'name',
          'slug',
          'grade',
          {
            image_preview: [
              {
                file: imageFields
              }
            ],
            image_work: [
              {
                file: imageFields
              }
            ],
            category: ['id', 'name', 'slug']
          }
        ]
      })
    );

    const aggregateCount = await directusClient.request<{ count: number }[]>(
      readItems(collectionName, {
        filter,
        aggregate: { count: '*' }
      })
    );

    const itemsMapped = items.map(item => mapSpecialistPreviewFields(item));
    // На клиенте (фильтр расписания) blurhash-плейсхолдеры не используются, а их
    // расчёт в браузере — это лишний fetch картинки + base64-кодирование на
    // каждое изображение каждого специалиста, блокирующий рендер дропдауна.
    const itemsMappedWithPlaceholder = skipPlaceholders
      ? itemsMapped
      : await Promise.all(
          itemsMapped.map(async item => {
            return {
              ...item,
              image_preview: await Promise.all(
                item.image_preview.map(async image => {
                  return {
                    ...image,
                    placeholder_data_url: !isGIF(image.type)
                      ? await getPlaceholderDataUrl(image.url)
                      : undefined
                  };
                })
              ),
              image_work: await Promise.all(
                item.image_work.map(async image => {
                  return {
                    ...image,
                    placeholder_data_url: !isGIF(image.type)
                      ? await getPlaceholderDataUrl(image.url)
                      : undefined
                  };
                })
              )
            };
          })
        );

    return {
      items: itemsMappedWithPlaceholder,
      page,
      pageTotal: Math.ceil(aggregateCount[0].count / limit),
      limit,
      total: Number(aggregateCount[0].count)
    };
  } catch (error) {
    return {
      items: [],
      page,
      pageTotal: 0,
      limit,
      total: 0
    };
  }
}
